import React, { useContext } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useModal } from "react-modal-hook";
import {
  GroupRoleType,
  usePublicUserLoginMutation,
} from "../../../generated/graphql";
import { useStatusToasts } from "../../../hooks/useStatusToasts";
import { spacing } from "../../../stitches.config";
import { track } from "../../../utils/tracking";
import { AuthContext } from "../../Authorization/AuthContext";
import {
  ContentSection,
  HeaderSection,
  UpdatedContainer,
} from "../../Common/__styles__/Modal";
import { Button } from "../../Common/Button";
import Modal from "../../Common/Modal";
import { Title } from "../../Common/Typography";
import { LoginForm } from "../__styles__/LoginPage";
import {
  ProvideCredentialsForm,
  ProvideCredentialsFormProps,
} from "../ProvideCredentialsForm";
import { PublicUserContext } from "./PublicUserContext";
import { useCreatePublicUserAccountModal } from "./PublicUserCreateAccountModal";

type UsePublicUserLoginModalProps = {
  onLoginSuccess: () => void;
  onCreateAccountClick: () => void;
};

//Use the useWrappedPublicUserLoginModal instead of this function as it handles the login and create account modals together.
//This function is exported for tests.
export const usePublicUserLoginModal = ({
  onLoginSuccess,
  onCreateAccountClick,
}: UsePublicUserLoginModalProps) => {
  const [showModal, hideModal] = useModal(
    () => (
      <Modal onRequestClose={hideModal}>
        <PublicUserLoginModalContent
          onSuccess={() => {
            onLoginSuccess();
            hideModal();
          }}
          onCreateAccountClick={onCreateAccountClick}
        />
      </Modal>
    ),
    []
  );

  return [showModal, hideModal] as const;
};

export type ProvideCredentialsFormStructure = {
  email?: Maybe<string>;
  password?: Maybe<string>;
};

const PublicUserLoginModalContent = ({
  onSuccess,
  onCreateAccountClick,
}: {
  onSuccess: () => void;
  onCreateAccountClick: () => void;
}) => {
  const { t } = useTranslation();
  const useFormProps = useForm<ProvideCredentialsFormStructure>();
  const { addErrorToast } = useStatusToasts();
  const [login, { data }] = usePublicUserLoginMutation({
    onCompleted: data => {
      if (data.publicUserLogin.ok) {
        track("Public user logged in");
        onSuccess();
      }
    },
    onError: () => {
      addErrorToast(t("sign-in-unexpected-network-error"));
    },
  });
  const error = data?.publicUserLogin.error ?? null;

  const handleLogin = async ({
    email,
    password,
  }: Parameters<ProvideCredentialsFormProps["onLogin"]>[0]) => {
    await login({
      variables: { email, password },
    });
  };

  return (
    <FormProvider {...useFormProps}>
      <UpdatedContainer size="small">
        <HeaderSection>
          <Title size="large" type="regular">
            {t("sign-in-to-your-account")}
          </Title>
        </HeaderSection>
        <ContentSection>
          <LoginForm>
            <ProvideCredentialsForm error={error} onLogin={handleLogin} />
            <div style={{ marginTop: spacing.m.toString() }}>
              <Button
                styleVariant="buttonLink"
                stretch
                onClick={onCreateAccountClick}
                style={{ height: "auto" }}
              >
                {t("create-account")}
              </Button>
            </div>
          </LoginForm>
        </ContentSection>
      </UpdatedContainer>
    </FormProvider>
  );
};

const useWrappedPublicUserLoginModal = () => {
  const { account } = useContext(AuthContext);
  const { refetchCurrentPublicUser } = useContext(PublicUserContext);

  const onCreateAccountClick = () => {
    hideSignIn();
    showCreateAccount();
  };

  const onSignInClick = () => {
    hideCreateAccount();
    showSignIn();
  };

  const [showCreateAccount, hideCreateAccount] =
    useCreatePublicUserAccountModal({
      onSignInClick,
      publicUserGroups:
        account?.userGroups.filter(
          userGroup => userGroup.memberType === GroupRoleType.PUBLIC_USER
        ) ?? [],
      minimumPasswordLength:
        account?.securityConfiguration?.minimumPasswordLength,
    });

  const [showSignIn, hideSignIn] = usePublicUserLoginModal({
    onLoginSuccess: () => {
      void refetchCurrentPublicUser();
    },
    onCreateAccountClick,
  });

  const hideAllModals = () => {
    hideCreateAccount();
    hideSignIn();
  };

  return [showSignIn, hideAllModals] as const;
};

export default useWrappedPublicUserLoginModal;
